export const brand = {
  name: '50 Fiches Visuelles de Placement des Électrodes en Kinésithérapie',
  promise: '+50 Fiches Visuelles de Placement des Électrodes',
  promiseAccent: 'en Kinésithérapie',
  subtitle:
    'Consultez en quelques secondes où placer les électrodes pour le TENS, le NMES, la FES et les courants interférentiels. Chaque fiche réunit l’anatomie, le positionnement, les électrodes, les paires et les canaux dans une seule référence visuelle.',
  mockupCaption: '50 Fiches Visuelles de Placement des Électrodes + 4 Bonus Professionnels',
}

export const heroBadges: string[] = ['Accès immédiat', 'Imprimable', 'Accès à vie']

export interface Stat {
  value: string
  label: string
}

export const stats: Stat[] = [
  { value: '50', label: 'fiches visuelles' },
  { value: '4', label: 'modalités principales' },
  { value: '100', label: 'références de points moteurs' },
  { value: '4', label: 'bonus professionnels' },
]

export const proofNote =
  'Un système visuel de consultation pour les étudiants et les professionnels en Kinésithérapie'

export const universities: string[] = ['TENS', 'NMES', 'FES', 'Interférentiel']

export interface PainGroup {
  audience: string
  points: string[]
}

export const painGroups: PainGroup[] = [
  {
    audience: 'Si vous êtes étudiant(e) en Kinésithérapie',
    points: [
      'Vous étudiez l’électrothérapie, mais vous hésitez ensuite sur le placement des électrodes',
      'Vous comprenez le TENS, le NMES ou la FES en théorie, mais vous avez du mal à visualiser leur application',
      'Vous avez des notes, des captures d’écran et des PDF dispersés et vous perdez du temps à chercher ce dont vous avez besoin',
      'Vous confondez parfois les électrodes, les paires ou les canaux lorsque les configurations deviennent plus complexes',
    ],
  },
  {
    audience: 'Si vous êtes déjà kinésithérapeute',
    points: [
      'Vous souhaitez vérifier rapidement un placement avant une application',
      'Vous ne voulez pas dépendre uniquement de votre mémoire pour vous rappeler chaque région',
      'Vous avez besoin de regrouper vos références d’électrothérapie au même endroit',
      'Vous souhaitez pouvoir consulter les fiches depuis votre téléphone, votre tablette ou les imprimer pour les garder dans votre espace de travail',
    ],
  },
]

export const solutionPoints: string[] = [
  '50 fiches visuelles : trouvez rapidement la région dont vous avez besoin.',
  'Positionnement illustré : visualisez directement les électrodes sur l’anatomie.',
  'Électrodes et canaux identifiés : E1, E2, E3, E4, CH1 et CH2 lorsque nécessaire.',
  'Organisé par modalité : TENS, NMES, FES et interférentiel.',
  'Repères anatomiques clairs : identifiez facilement chaque région et chaque groupe musculaire.',
  '4 bonus professionnels : paramètres, sécurité, points moteurs et fiches de référence rapide.',
]

export interface Module {
  number: string
  title: string
  count: string
  detail: string
  summary: string
  closing: string
  spine: string
}

export const modules: Module[] = [
  {
    number: '01',
    title: 'TENS',
    count: '15',
    detail: '15 fiches visuelles',
    summary:
      'Région cervicale, cervico-thoracique, trapèze, région scapulaire, épaule, lombaires, sacro-iliaque, coude, poignet, main, hanche, genou, région postérieure du genou, cheville et pied.',
    closing: 'Des positionnements régionaux pour une consultation visuelle rapide.',
    spine: 'bg-blue-400',
  },
  {
    number: '02',
    title: 'NMES',
    count: '18',
    detail: '18 fiches visuelles',
    summary:
      'Quadriceps, vaste médial, ischio-jambiers, grand fessier, moyen fessier, tibial antérieur, gastrocnémien, soléaire, fibulaires, deltoïdes, biceps, triceps, fléchisseurs et extenseurs.',
    closing: 'Des repères musculaires et une répartition des électrodes clairement identifiés.',
    spine: 'bg-blue-600',
  },
  {
    number: '03',
    title: 'FES + Applications Fonctionnelles',
    count: '9',
    detail: '9 fiches visuelles',
    summary:
      'Dorsiflexion, pied tombant, extension et flexion du genou, extension de hanche, abduction de l’épaule, flexion et extension du coude et extension du poignet.',
    closing: 'Associez la stimulation à la réponse fonctionnelle recherchée.',
    spine: 'bg-blue-700',
  },
  {
    number: '04',
    title: 'Interférentiel + Techniques de Placement',
    count: '8',
    detail: '8 fiches visuelles',
    summary:
      'Configuration bipolaire/prémodulée, tétrapolaire, lombaires, genou, épaule, placement bipolaire longitudinal, quadripolaire et point moteur vs. placement conventionnel.',
    closing: 'Visualisez les canaux, les paires et la logique de répartition.',
    spine: 'bg-blue-900',
  },
]

export const mapIncludes: string[] = [
  'Courant ou modalité utilisé',
  'Région anatomique',
  'Groupe musculaire ou objectif',
  'Électrodes identifiées',
  'Canaux et paires',
  'Positionnement visuel',
  'Réponse recherchée',
  'Points d’attention et précautions',
]

export interface GalleryItem {
  title: string
  caption: string
  image: string
}

export const gallery: GalleryItem[] = [
  {
    title: 'TENS — Région cervicale postérieure',
    caption:
      'Muscles paravertébraux cervicaux, trapèze supérieur et muscles sous-occipitaux : 4 électrodes réparties sur 2 canaux, avec les effets attendus et les précautions.',
    image:
      'https://cdn.magicpatterns.com/uploads/gmW3CPU3L66KnaZBgM1A1u/Captura_de_pantalla_2026-09-28_a_la(s)_1.08.53_a._m..png',
  },
  {
    title: 'TENS — Épaule',
    caption:
      'Deltoïde, coiffe des rotateurs, trapèze supérieur et infra-épineux : placement autour de la région, avec les canaux et les paires identifiés.',
    image:
      'https://cdn.magicpatterns.com/uploads/25XovrUHqsS95EGyqvM21f/Captura_de_pantalla_2026-09-28_a_la(s)_1.09.14_a._m..png',
  },
  {
    title: 'TENS — Région lombaire',
    caption:
      'Érecteurs du rachis, multifides et carré des lombes : électrodes parallèles à la colonne, de L2 à L5, avec les précautions essentielles.',
    image:
      'https://cdn.magicpatterns.com/uploads/6ioWYiNXUKpJZ48VRixW2Q/Captura_de_pantalla_2026-09-28_a_la(s)_1.09.31_a._m..png',
  },
  {
    title: 'FES / NMES — Abduction de l’épaule',
    caption:
      'Deltoïde moyen : vue antérieure et latérale, 2 électrodes alignées sur les fibres et la réponse fonctionnelle recherchée.',
    image:
      'https://cdn.magicpatterns.com/uploads/eiDpAzsmQsEzTizEqRyAUq/Captura_de_pantalla_2026-09-28_a_la(s)_1.09.50_a._m..png',
  },
  {
    title: 'TENS — Poignet',
    caption:
      'Carpe et structures périarticulaires : vue dorsale et palmaire, 4 électrodes sur les extenseurs et les fléchisseurs réparties sur 2 canaux.',
    image:
      'https://cdn.magicpatterns.com/uploads/4toKSehEvc9f9edb1ZbPaC/Captura_de_pantalla_2026-09-28_a_la(s)_1.10.07_a._m..png',
  },
  {
    title: 'TENS — Cheville',
    caption:
      'Région périarticulaire de la cheville : vue antérieure et latérale, 4 électrodes autour des malléoles réparties sur 2 canaux.',
    image:
      'https://cdn.magicpatterns.com/uploads/cEqe1SCfLeuktKxxY6GWCX/Captura_de_pantalla_2026-09-28_a_la(s)_1.10.27_a._m..png',
  },
]

export interface Bonus {
  tag: string
  title: string
  subtitle: string
  value: string
  benefit: string
  detail: string
  image: string
  alt: string
}

export const bonusesTotalValue = '27,60 €'

export const bonuses: Bonus[] = [
  {
    tag: 'Bonus 01',
    title: 'Guide Rapide des Paramètres d’Électrothérapie',
    subtitle: 'TENS • NMES • FES • INTERFÉRENTIEL',
    value: '6,90 €',
    benefit:
      'Fréquence, largeur d’impulsion, intensité, rampes, cycles de travail et durées expliqués à l’aide de tableaux et de références visuelles.',
    detail: '30 pages illustrées et pratiques',
    image: 'https://cdn.magicpatterns.com/uploads/pn7ttNAXFBwcS8KoqQ7Grq/image.png',
    alt: 'Mockup du Guide Rapide des Paramètres d’Électrothérapie',
  },
  {
    tag: 'Bonus 02',
    title: 'Check-list de Sécurité et des Contre-indications',
    subtitle: 'Avant • Pendant • Après',
    value: '6,90 €',
    benefit:
      'Un guide visuel pour vérifier la peau, la sensibilité, les dispositifs implantés, les électrodes, les zones de précaution, la réponse du patient et les signes indiquant qu’il faut interrompre l’application et réévaluer.',
    detail: '30 pages + check-list imprimable',
    image: 'https://cdn.magicpatterns.com/uploads/9viqqsA3h1uSsPzEWwaa1k/image.png',
    alt: 'Mockup de la Check-list de Sécurité et des Contre-indications',
  },
  {
    tag: 'Bonus 03',
    title: 'Atlas Visuel des Points Moteurs',
    subtitle: 'Localisation rapide par groupe musculaire',
    value: '8,90 €',
    benefit:
      'Biceps, triceps, deltoïdes, quadriceps, ischio-jambiers, tibial antérieur, gastrocnémien, fessiers, fléchisseurs, extenseurs et plus encore.',
    detail: '100 illustrations anatomiques avec repères visuels',
    image: 'https://cdn.magicpatterns.com/uploads/3isNZPEXX96wa5DPNS33Ud/image.png',
    alt: 'Mockup de l’Atlas Visuel des Points Moteurs',
  },
  {
    tag: 'Bonus 04',
    title: 'Fiches de Référence Rapide par Type de Courant',
    subtitle: 'TENS • NMES • FES • INTERFÉRENTIEL',
    value: '4,90 €',
    benefit:
      '4 fiches visuelles qui résument l’objectif, les principaux paramètres, la réponse recherchée, les placements courants et les précautions.',
    detail: 'Un courant par fiche. L’essentiel en quelques secondes.',
    image: 'https://cdn.magicpatterns.com/uploads/6ZPuMkAYM4EWUL9wwSGXsv/image.png',
    alt: 'Mockup des Fiches de Référence Rapide par Type de Courant',
  },
]

export interface Testimonial {
  initials: string
  quote: string
  author: string
  role: string
  city: string
  note: string
}

export const testimonials: Testimonial[] = [
  {
    initials: 'CR',
    quote:
      'En électrothérapie, j’avais toujours mes informations réparties entre plusieurs notes. Avec les fiches, je peux voir la région, les électrodes et les canaux au même endroit, ce qui rend la consultation beaucoup plus claire.',
    author: 'Camille R.',
    role: 'Étudiante en Kinésithérapie',
    city: 'Lyon',
    note: '[MAQUETTE — À REMPLACER PAR UN AVIS CLIENT RÉEL]',
  },
  {
    initials: 'TM',
    quote:
      'Je les garde sur ma tablette et je m’en sers comme référence rapide. Je cherche la région et, en quelques secondes, j’ai le schéma visuel sous les yeux.',
    author: 'Thomas M.',
    role: 'Kinésithérapeute',
    city: 'Bordeaux',
    note: '[MAQUETTE — À REMPLACER PAR UN AVIS CLIENT RÉEL]',
  },
  {
    initials: 'CD',
    quote:
      'Ce qui m’aide le plus, c’est de pouvoir visualiser les électrodes directement sur l’anatomie. Pour étudier le NMES et la FES, c’est beaucoup plus clair.',
    author: 'Chloé D.',
    role: 'Étudiante en Kinésithérapie',
    city: 'Lille',
    note: '[MAQUETTE — À REMPLACER PAR UN AVIS CLIENT RÉEL]',
  },
]

export interface FaqItem {
  question: string
  answer: string
}

export const faqs: FaqItem[] = [
  {
    question: 'Quand vais-je recevoir le matériel ?',
    answer:
      'L’accès est immédiat après confirmation du paiement. Vous recevez le matériel numérique et pouvez commencer à le consulter directement depuis votre appareil.',
  },
  {
    question: 'Est-ce adapté si je suis étudiant(e) en Kinésithérapie ?',
    answer:
      'Oui. Le matériel est conçu pour faciliter la compréhension visuelle du placement des électrodes et compléter l’étude de l’électrothérapie. Il ne remplace pas vos cours, votre bibliographie ni votre formation professionnelle.',
  },
  {
    question: 'Est-ce adapté si je suis déjà kinésithérapeute ?',
    answer:
      'Oui. En plus de servir de support d’étude, il est organisé pour fonctionner comme un outil de consultation rapide. Vous pouvez rechercher la région ou la modalité dont vous avez besoin et visualiser la référence correspondante.',
  },
  {
    question: 'Quelles modalités sont incluses ?',
    answer:
      'Le système porte principalement sur le TENS, le NMES, la FES et le courant interférentiel. Il comprend également des fiches spécifiques sur les configurations bipolaires, quadripolaires et les points moteurs.',
  },
  {
    question: 'Puis-je consulter les fiches depuis mon téléphone ?',
    answer:
      'Oui. Le matériel étant numérique, vous pouvez le consulter depuis votre téléphone, votre tablette ou votre ordinateur.',
  },
  {
    question: 'Puis-je imprimer les fiches ?',
    answer:
      'Oui. Les fichiers sont préparés en haute qualité et peuvent être imprimés pour un usage personnel.',
  },
  {
    question: 'Dans quelle offre les bonus sont-ils inclus ?',
    answer:
      'Les 4 bonus sont inclus avec le Système complet : Guide des Paramètres, Check-list de Sécurité, Atlas des Points Moteurs et Fiches de Référence par Type de Courant.',
  },
  {
    question: 'Le matériel m’indique-t-il exactement quels paramètres utiliser pour chaque patient ?',
    answer:
      'Non. Le guide propose des références éducatives pour comprendre les paramètres et faciliter leur consultation. Le choix final doit être individualisé selon l’évaluation, l’objectif, le patient, l’appareil utilisé et le jugement professionnel.',
  },
  {
    question: 'Le matériel remplace-t-il l’évaluation professionnelle ?',
    answer:
      'Non. Les fiches sont des outils éducatifs et de consultation visuelle. Elles ne remplacent pas l’évaluation clinique, la formation professionnelle, les instructions du fabricant ni les protocoles de l’établissement.',
  },
  {
    question: 'L’accès est-il à vie ?',
    answer: 'Oui. Vous effectuez un paiement unique et le matériel reste à votre disposition.',
  },
  {
    question: 'Peut-il être utilisé comme support de consultation ?',
    answer:
      'Oui. Le système a été organisé précisément pour faciliter la recherche visuelle par région, muscle ou modalité. Il doit toujours être utilisé dans le cadre de la formation et du jugement professionnel correspondants.',
  },
]

export const scarcity = {
  total: 50,
  left: 42,
  note: 'Prix spécial pour les 50 premiers.',
  nextPrice: '29 €',
}

export const checkoutUrl = 'https://fichesvisuelles.impultienda.ar'

export interface Offer {
  name: string
  summary: string
  finalSummary: string
  includes: string[]
  listPrice: string
  totalValue: string
  price: string
  discount: string
  savings: string
  cta: string
  finalCta: string
}

export const offer: Offer = {
  name: '50 Fiches Visuelles de Placement des Électrodes — Système complet',
  summary: '50 fiches + Atlas de 100 points moteurs + 3 outils professionnels',
  finalSummary: '50 fiches visuelles + 4 bonus professionnels',
  includes: [
    '50 Fiches Visuelles de Placement des Électrodes',
    'TENS, NMES, FES et interférentiel',
    'Membres supérieurs et inférieurs',
    'Colonne, tronc, épaule et hanche',
    'Électrodes, paires et canaux identifiés',
    'Applications fonctionnelles',
    'Guide Rapide des Paramètres',
    'Check-list de Sécurité et des Contre-indications',
    'Atlas Visuel des Points Moteurs',
    'Fiches de Référence Rapide par Type de Courant',
    'PDF haute qualité',
    'Imprimable',
    'Accès à vie',
  ],
  listPrice: '39 €',
  totalValue: '66,60 €',
  price: '19 €',
  discount: '-51 %',
  savings: '20 €',
  cta: 'Je veux les 50 Fiches + 4 Bonus',
  finalCta: 'Je veux les 50 Fiches maintenant',
}

export const pricingRows: { label: string; value: string }[] = [
  { label: '50 Fiches Visuelles de Placement des Électrodes (PDF imprimable)', value: '39 €' },
  { label: '4 bonus professionnels inclus', value: '27,60 €' },
]

export const heroImage = 'https://cdn.magicpatterns.com/uploads/imvJNCVNe759cguLXiMLow/mockup-fr.png'

export interface PurchaseNotice {
  name: string
  city: string
  product: string
}

export const purchaseNotices: PurchaseNotice[] = [
  { name: 'Léa R.', city: 'Marseille', product: '50 Fiches Visuelles + 4 bonus' },
  { name: 'Hugo B.', city: 'Nantes', product: '50 Fiches Visuelles + 4 bonus' },
  { name: 'Manon D.', city: 'Toulouse', product: '50 Fiches Visuelles + 4 bonus' },
]

export const footerLinks: { label: string; href: string }[] = [
  { label: 'Conditions Générales de Vente', href: '#terminos' },
  { label: 'Politique de Confidentialité', href: '#privacidad' },
  { label: 'Contact', href: '#contacto' },
]
